import { useState, useRef, useEffect, useMemo, useCallback, startTransition, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, FolderPlus, Upload, Search, Edit2, Save, X, File, FileClock, Tag, Plus, List, Eye, Filter, Send, Trash2,
  PlayCircle, Loader2, Check, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/apiFetch";
import { Textarea } from "@/components/ui/textarea";
import { ChevronDown, ChevronRight } from "lucide-react";
import { File as FileIcon, Paperclip } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface DocumentItem {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadDate: string;
  processed: 0 | 1;
  tags: string[] | null;
  // If your API includes a full S3 path, it will be picked up automatically below:
  // path?: string;
}

interface Category {
  id: string;
  name: string;
  documents: DocumentItem[];
}

interface ProjectData {
  id: number;
  name: string;
  description: string;
  categories: Category[];
}

interface ApiSearchResponse {
  response?: string;
  llm_answer?: string,
  sources?: Array<{
    path: string;
    score?: number;
    raw_score?: string;
    needle_coverage?: string;
    needles_matched?: string;
    redacted?: boolean;
    privileged?: string;
    category?: string;
    match?: string;
  }>;
  match_count?: number;
  status?: string;
  total_occurrences?: number
  debug_info?: string;
  details?: string;
}

type AiAttachment = { name: string; url: string; size?: number };
type AiFileMeta = { id: string; name: string; size: number; type?: string };

type ChatMsg = {
  role: 'user' | 'assistant';
  content: string;
  attachments?: AiAttachment[];
};

function baseName(input: string): string {
  if (!input) return "";
  // strip scheme like "sandbox:" and query/hash
  input = input.replace(/^sandbox:\/*/i, "").split(/[?#]/)[0];
  // take last segment of / or \
  const parts = input.split(/[/\\]/);
  return parts[parts.length - 1] || "";
}

function Html({ html }: { html: string }) {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

const AttachmentsList = memo(function AttachmentsList({
  items,
  onRemove,
  onDownload,
}: {
  items: AiFileMeta[];
  onRemove?: (id: string) => void;
  onDownload?: (name: string) => void;
}) {
  return (
    <div className="mt-2 space-y-1">
      {items.map(item => (
        <div key={item.id} className="text-sm flex items-center gap-2">
          <FileIcon className="h-5 w-5 text-primary pointer-events-none" />
          <a
            href="#"
            className="underline underline-offset-2 hover:opacity-80"
            title={item.name}
            onClick={e => {
              e.preventDefault();
              onDownload?.(item.name);
            }}
          >
            {baseName(item.name) || "download"}
          </a>
          <span className="text-muted-foreground">({Math.round(item.size / 1024)} KB)</span>
          {onRemove ? (
            <button className="ml-1 text-xs opacity-70 hover:opacity-100" onClick={() => onRemove(item.id)}>
              remove
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
});

const AiHistoryList = memo(function AiHistoryList({
  history,
  lastRef,
}: {
  history: ChatMsg[];
  lastRef: React.RefObject<HTMLDivElement>;
}) {
  return (
    <>
      {history.map((m, i) => (
        <div
          key={i}
          className={`mb-2 ${m.role === "assistant" ? "text-foreground" : "text-foreground/80"}`}
          ref={i === history.length - 1 ? lastRef : undefined}
        >
          <span className="font-semibold mr-2">
            {m.role === "assistant" ? "Assistant" : "You"}:
          </span>
          <span className="whitespace-pre-wrap">{m.content}</span>

          {m.attachments?.length ? (
            <div className="mt-2 space-y-1">
              {m.attachments.map((a, idx) => (
                <div key={`${a.url}-${idx}`} className="text-sm flex items-center gap-2">
                  <FileIcon className="h-5 w-5 text-primary pointer-events-none" />
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:opacity-80"
                    onClick={e => {
                      e.preventDefault();
                      downloadWithHeaders(a.url, a.name);
                    }}
                  >
                    {baseName(a.name) || "download"}
                  </a>
                  {typeof a.size === "number" ? (
                    <span className="text-muted-foreground">({Math.round(a.size / 1024)} KB)</span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </>
  );
});

function basenameOnly(p: string) {
  return (p || "").split(/[\\/]/).pop() || "";
}

const Project = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isAuthenticated } = useAuth();

  const [project, setProject] = useState<ProjectData>({ id: parseInt(id || "1"), name: "", description: "", categories: [] });
  const [editedName, setEditedName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedCategoryView, setSelectedCategoryView] = useState<string>("0");
  const [editingFile, setEditingFile] = useState("");
  const [newTag, setNewTag] = useState("");

  // Textareas
  const [keywordQuery, setKeywordQuery] = useState("");
  const [refineQuery, setRefineQuery] = useState("");

  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const [viewMode, setViewMode] = useState<{ [key: string]: 'grid' | 'list' }>({});
  const [filePage, setFilePage] = useState<{ [key: string]: number }>({});
  const [isSearching, setIsSearching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [limitFilesReturned, setLimitFilesReturned] = useState<number | undefined>();
  const [orSearch, setOrSearch] = useState<number | undefined>();
  const [responseOnly, setResponseOnly] = useState<number | undefined>();
  const [searchHistory, setSearchHistory] = useState<any[]>([]);
  const [isRefineMode, setIsRefineMode] = useState(false);
  const queryElement = typeof window !== 'undefined' ? document.getElementById("search_results") : null;
  const [lastNotice, setLastNotice] = useState<string | null>(null);
  const lastResultRef = useRef<HTMLDivElement>(null);
  const lastResultAiRef = useRef<HTMLDivElement>(null);
  const kwTypedRef = useRef(false);
  const suppressKwSuggestOnce = useRef(false);
  const aiTypedRef = useRef(false);
  const suppressAiSuggestOnce = useRef(false);
  const [remainingAiPaths, setRemainingAiPaths] = useState<string[]>([]);
  const [retryAt, setRetryAt] = useState<number>(0);
  const [secsLeft, setSecsLeft] = useState<number>(0);
  const aiFileInputRef = useRef<HTMLInputElement>(null);
  const [aiThreadId, setAiThreadId] = useState<string | null>(null);
  const [uploadingCat, setUploadingCat] = useState<string | null>(null);
  const [isContinuing, setIsContinuing] = useState(false);
  const [reportQueued, setReportQueued] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [isDocsRendering, setIsDocsRendering] = useState(false);
  const [aiDocsIsLoading, setAiDocsIsLoading] = useState(false);

  // Tabs & Chat
  const [activeTab, setActiveTab] = useState<'doc' | 'ai'>('doc');
  const [aiQuery, setAiQuery] = useState("");
  const [aiHistory, setAiHistory] = useState<ChatMsg[]>([]);
  const [aiIsLoading, setAiIsLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const keyLabelShow = useRef(false);
  // Intellisense states
  interface PromptSuggestion { prompt: string; uses: number; last_used_at: string; type?: number; }

  interface PromptSuggestion { prompt: string; uses: number; last_used_at: string; type?: number; }
  const [aiPromptSuggestions, setAiPromptSuggestions] = useState<PromptSuggestion[]>([]);
  const [showAiPromptSuggestions, setShowAiPromptSuggestions] = useState(false);
  const aiSuggestReqId = useRef(0);
  const [keywordSuggestions, setKeywordSuggestions] = useState<PromptSuggestion[]>([]);
  const [showKeywordSuggestions, setShowKeywordSuggestions] = useState(false);
  const kwSuggestReqId = useRef(0);
  const { toast } = useToast();

  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () =>
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });

  // NEW: processing UI state
  const [processStatus, setProcessStatus] = useState<'idle' | 'processing' | 'processed' | 'error'>('idle');
  const [processedFilesCount, setProcessedFilesCount] = useState<number>(0);
  const [dismissProcessedBadge, setDismissProcessedBadge] = useState<boolean>(false);
  const [isStartingProcessing, setIsStartingProcessing] = useState<boolean>(false);

  const NativeFile = (globalThis as any).File as typeof File;
  const aiFilesRef = useRef<Map<string, File>>(new Map());
  const [aiFileMeta, setAiFileMeta] = useState<AiFileMeta[]>([]);
  const [includeAllDocs, setIncludeAllDocs] = useState(false);
  const [projectAnalysisId, setProjectAnalysisId] = useState<number | null>(null);
  const analysisRequest = useRef<{ project: number; prompt: string; key: string } | null>(null);
  useEffect(() => {
    setProjectAnalysisId(null);
    analysisRequest.current = null;
  }, [project?.id]);

  // Count unprocessed docs across the entire project
  const unprocessedCount = useMemo(() => {
    let total = 0;
    for (const cat of project?.categories || []) {
      for (const d of cat?.documents || []) {
        if (!d.processed) total++;
      }
    }
    return total;
  }, [project]);

  // Helper: try to build S3 file list from project docs if paths are present
  const collectUnprocessedPaths = (): string[] => {
    const paths: string[] = [];
    for (const cat of project?.categories || []) {
      for (const d of cat?.documents || []) {
        // prefer explicit path-like field if your API provides it
        const anyDoc = d as unknown as { path?: string };
        if (!d.processed && anyDoc?.path) {
          paths.push(anyDoc.path);
        }
      }
    }
    return paths;
  };

  const stripExt = (n: string) => n.replace(/\.[^.]+$/, "");
  const toTxtName = (name: string) => stripExt(name) + ".txt"; // you already have this; keep it

  async function docsToAiFiles(docs: Array<{ id: number | string; name: string }>): Promise<number> {
      const toAddMeta: AiFileMeta[] = [];
      const toAddFiles: Array<[string, File]> = [];

      for (const d of docs) {
        try {
          // always fetch the TXT variant
          const res = await fetch(`/api/documents/${d.id}/download?stream=1&variant=txt`, { credentials: "include" });
          if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
          const blob = await res.blob();

          // (1) ensure the file's *name* ends with .txt
          const txtName = toTxtName(d.name);

          // (2) ensure the file's *type* is text/plain
          const NativeFile = (globalThis as any).File as typeof File;
          const file = new NativeFile([blob], txtName, { type: "text/plain" });

          // (3) remove any existing attachment with the same basename but a different extension
          const stem = stripExt(d.name).toLowerCase();
          for (const [idKey, existing] of Array.from(aiFilesRef.current.entries())) {
            const existingStem = stripExt(existing.name).toLowerCase();
            if (existingStem === stem && !existing.name.toLowerCase().endsWith(".txt")) {
              aiFilesRef.current.delete(idKey);
              // also remove from UI list
              startTransition(() => {
                setAiFileMeta(prev => prev.filter(m => m.id !== idKey));
              });
            }
          }

          // (4) de-dupe against current map by *name* (txtName) and *size*
          const exists = Array.from(aiFilesRef.current.values()).some(
            f => f.name === file.name && f.size === file.size
          );
          if (exists) continue;

          const id = crypto.randomUUID?.() ?? `${file.name}:${file.size}:${Date.now()}`;
          toAddFiles.push([id, file]);
          toAddMeta.push({ id, name: file.name, size: file.size, type: file.type || "" });
        } catch (e) {
          console.error("Failed to fetch document", d, e);
        }
      }

      // Synchronously update the Map (ready immediately)
      for (const [id, f] of toAddFiles) aiFilesRef.current.set(id, f);

      // Light UI update
      startTransition(() => {
        setAiFileMeta(prev => [...prev, ...toAddMeta]);
      });

      return toAddFiles.length;
    }

  
  const uploadSelectedToGeneralAI = useCallback(
    async (allDocs: Array<{ id: number | string; name: string }>) => {
      setAiDocsIsLoading(true);

      // Define selected *here*
      const selectedDocs = (allDocs ?? []).filter(d => selectedDocIds.has(d.id));

      const count = selectedDocs.length
        ? await docsToAiFiles(selectedDocs)  // <-- no bare `selected`
        : 0;

      if (count > 0) {
        setActiveTab("ai");
        requestAnimationFrame(scrollToBottom);
      } else {
        // optional: toast("No files selected or fetch failed");
      }

      setAiDocsIsLoading(false);
    },
    [selectedDocIds, docsToAiFiles]
  );

  useEffect(() => {
    if (!retryAt) { setSecsLeft(0); return; }
    const tick = () => setSecsLeft(Math.max(0, Math.ceil((retryAt - Date.now())/1000)));
    tick();
    const idt = setInterval(tick, 250);
    return () => clearInterval(idt);
  }, [retryAt]);

  useEffect(() => {
    if (!isAuthenticated) navigate("/login");
    apiFetch(`/api/projects/${id}`).then(res => {
      setProject(res.data);
      setEditedName(res.data.name);
    }).catch(console.error);
  }, [isAuthenticated, navigate, id]);

  useEffect(() => {
    if (!project?.categories?.length) return;
    setSelectedCategoryView("0");
    const vm: { [key: string]: 'grid' | 'list' } = {};
    const pg: { [key: string]: number } = {};
    project.categories.forEach(cat => { vm[cat.id] = 'list'; pg[cat.id] = 1; });
    setViewMode(vm);
    setFilePage(pg);
  }, [project]);

  useEffect(() => {
    setSelectedDocIds(new Set());
  }, [selectedCategoryView, expanded]);

  // Poll processing status every 30s, and once on load
  useEffect(() => {
    if (!project?.id) return;
    const check = async () => {
      try {
        const res = await apiFetch(`/api/projects/${project.id}/process-status`);
        const data = (res as any)?.data ?? res;
        const status = (data?.status ?? 'idle') as 'idle' | 'processing' | 'processed' | 'error';

        if (status === 'processing') {
          setProcessStatus('processing');
          setDismissProcessedBadge(false);
        } else if (status === 'processed') {
          setProcessStatus('processed');
          setProcessedFilesCount(Number(data?.processed_count ?? data?.count ?? 0));
          setDismissProcessedBadge(false);
          // refresh project to reflect updated processed flags
          try {
            const refreshed = await apiFetch(`/api/projects/${project.id}`);
            setProject(refreshed.data);
          } catch {}
        } else if (status === 'error') {
          setProcessStatus('error');
          setDismissProcessedBadge(false);
        } else {
          setProcessStatus('idle');
        }
      } catch {
        // ignore network/404 here; UI will fall back to idle
      }
    };

    check();
    const intId = setInterval(check, 60000);
    return () => clearInterval(intId);
  }, [project?.id]);

  const toggleDocChecked = useCallback((id: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const includeSelectedIntoKeywords = useCallback((docs: DocumentItem[]) => {
    const selectedNames = docs
      .filter(d => selectedDocIds.has(d.id))
      .map(d => d.name)
      .filter(Boolean);

    if (selectedNames.length === 0) return;

    const existingTokens = (keywordQuery || "")
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);

    const merged = [...existingTokens, ...selectedNames];
    const seen = new Set<string>();
    const deduped = merged.filter(n => {
      const k = n.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    setKeywordQuery(deduped.join(", "));
  }, [selectedDocIds, keywordQuery]);

  const splitKeywords = (s: string) =>
    s.split(",").map(t => t.trim()).filter(Boolean);

  const currentKeywordToken = (s: string) => {
    const idx = s.lastIndexOf(",");
    return (idx === -1 ? s : s.slice(idx + 1)).trim();
  };

  const clearAllSuggestions = () => {
    setShowAiPromptSuggestions(false);
    setShowKeywordSuggestions(false);
    setAiPromptSuggestions([]);
    setKeywordSuggestions([]);
    aiSuggestReqId.current++;
    kwSuggestReqId.current++;
  };

  const applyKeywordSuggestion = (input: string, suggestion: string) => {
    const idx = input.lastIndexOf(",");
    if (idx === -1) return suggestion;
    const prefix = input.slice(0, idx + 1).replace(/\s+$/, "");
    return `${prefix} ${suggestion}`.replace(/\s+,/g, ",");
  };

  // Intellisense for AI prompt (type=1). Only when refine textarea is shown.
  useEffect(() => {
    if (suppressAiSuggestOnce.current) {
        suppressAiSuggestOnce.current = false;
        setAiPromptSuggestions([]); setShowAiPromptSuggestions(false);
        return;
    }

    if (!aiTypedRef.current) {
        setAiPromptSuggestions([]); setShowAiPromptSuggestions(false);
        return;
    }
    aiTypedRef.current = false;

    const q = refineQuery.trim();
    if (!project?.id || q.length < 3) {
      setAiPromptSuggestions([]); setShowAiPromptSuggestions(false); return;
    }
    const myReq = ++aiSuggestReqId.current;
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/projects/${project.id}/search-prompts?q=${encodeURIComponent(q)}&limit=8&type=1`);
        const list = (res?.data ?? res) as PromptSuggestion[];
        if (aiSuggestReqId.current === myReq) {
          setAiPromptSuggestions(list || []);
          setShowAiPromptSuggestions((list?.length ?? 0) > 0);
        }
      } catch {
        if (aiSuggestReqId.current === myReq) {
          setAiPromptSuggestions([]); setShowAiPromptSuggestions(false);
        }
      }
    }, 200);
    return () => clearTimeout(t);
  }, [refineQuery, project?.id, isRefineMode]);

  // Intellisense for Keywords (type=0). Based on the LAST token only.
  useEffect(() => {
    if (suppressKwSuggestOnce.current) {
        suppressKwSuggestOnce.current = false;
        setKeywordSuggestions([]); setShowKeywordSuggestions(false);
        return;
    }

    if (!kwTypedRef.current) {
        setKeywordSuggestions([]); setShowKeywordSuggestions(false);
        return;
    }
    kwTypedRef.current = false;

    const token = currentKeywordToken(keywordQuery);
    if (!project?.id || token.length < 2) {
      setKeywordSuggestions([]); setShowKeywordSuggestions(false); return;
    }
    const myReq = ++kwSuggestReqId.current;
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/projects/${project.id}/search-prompts?q=${encodeURIComponent(token)}&limit=8&type=0`);
        const list = (res?.data ?? res) as PromptSuggestion[];
        if (kwSuggestReqId.current === myReq) {
          setKeywordSuggestions(list || []);
          setShowKeywordSuggestions((list?.length ?? 0) > 0);
        }
      } catch {
        if (kwSuggestReqId.current === myReq) {
          setKeywordSuggestions([]); setShowKeywordSuggestions(false);
        }
      }
    }, 200);
    return () => clearTimeout(t);
  }, [keywordQuery, project?.id]);

  const getVisibleDocuments = (catId: string) => {
    const cat = project.categories.find(c => c.id === catId);
    if (!cat) return [];
    return cat.documents.slice(0, (filePage[catId] || 1) * 50);
  };

  // Helpers used elsewhere
  function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
  }

  function getMostRecentPathsWithResults(history: Array<{ sources?: Array<{ path?: string }> }>): string[] | null {
    for (let i = history.length - 1; i >= 0; i--) {
      const srcs = history[i]?.sources;
      if (Array.isArray(srcs) && srcs.length > 0) {
        const paths = srcs.map(s => s?.path).filter((p): p is string => !!p);
        if (paths.length > 0) {
          const seen = new Set<string>();
          const unique = paths.filter(p => (seen.has(p) ? false : (seen.add(p), true)));
          return unique;
        }
      }
    }
    return null;
  }

    function filenameFromContentDisposition(cd: string | null): string {
      if (!cd) return "";
      // RFC 5987: filename*=UTF-8''...
      const star = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(cd);
      if (star) return decodeURIComponent(star[1]);
      // filename="name.ext" (or without quotes)
      const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
      return plain ? plain[1] : "";
    }

    async function downloadWithHeaders(url: string, filename?: string): Promise<string> {
      const res = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "include",
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || res.statusText);
      }

      // Decide the filename (CD header -> provided -> URL)
      let finalName =
        filenameFromContentDisposition(res.headers.get("Content-Disposition")) ||
        filename ||
        baseName(url);

      // If a path slipped through, reduce to basename
      finalName = baseName(finalName) || "download";

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = finalName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }

      return finalName;
    }

  // Kick off processing
  const startProcessing = async () => {
    try {
      setIsStartingProcessing(true);
      setProcessStatus('processing');
      setDismissProcessedBadge(false);

      // Try to send explicit file list if we have full paths; else let backend derive by project
      const file_paths = collectUnprocessedPaths();
      const payload: any = file_paths.length ? { file_paths } : {};

      await fetch("/sanctum/csrf-cookie", { credentials: "include" });
      const headers: HeadersInit = { Accept: "application/json", "Content-Type": "application/json" };
      const xsrfToken = getCookie("XSRF-TOKEN");
      if (xsrfToken) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);

      const resp = await fetch(`/api/projects/${project.id}/process-documents/ocr`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const msg = await resp.text().catch(() => "");
        throw new Error(msg || `HTTP ${resp.status}`);
      }

      // Backend will create a process_queue row with status=processing and start python
      // UI remains in "Processing…" until the poller flips it.
    } catch (e: any) {
      console.error("Start processing failed:", e);
      setProcessStatus('error');
      toast({ title: "Processing failed to start", description: e?.message || "Unknown error", variant: "destructive" });
    } finally {
      setIsStartingProcessing(false);
    }
  };

  const dismissProcessQueue = async () => {
    if (!project?.id) return;

    // CSRF for Sanctum
    await fetch("/sanctum/csrf-cookie", { credentials: "include" });
    const headers: HeadersInit = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    const xsrf = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/);
    if (xsrf) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrf[1]);

    const resp = await fetch(`/api/projects/${project.id}/process-queue`, {
      method: "DELETE",
      credentials: "include",
      headers,
    });
    if (!resp.ok && resp.status !== 204) {
      throw new Error(`Failed to clear process queue: HTTP ${resp.status}`);
    }
  };


  const handleSaveName = () => {
    setProject(prev => ({ ...prev, name: editedName }));
    setIsEditingName(false);
  };

  const handleCreateCategory = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;

    try {
      // Sanctum CSRF (no-op if already present)
        await fetch("/sanctum/csrf-cookie", { credentials: "include" });
        const headers: HeadersInit = {
          Accept: "application/json",
          "Content-Type": "application/json",
        };
        const xsrf = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/);
        if (xsrf) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrf[1]);

      const resp = await fetch(`/api/projects/${project.id}/create-category`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ name: trimmed }),
      });

      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(msg || resp.statusText);
      }

      // Option A: hard refresh page (as requested)
      window.location.reload();

      // Option B (alternative): re-fetch project data instead of reloading
      // await fetchProject(); // if you have a loader for project details
      // setShowNewCategory(false);

    } catch (e) {
      console.error("Create category failed:", e);
      // optional toast
      // toast({ title: "Create category failed", description: String(e), variant: "destructive" })
    }
  };


  async function uploadSelectedFiles(categoryId: string, list: FileList | null) {
    if (!list || !categoryId) return;

    try {
      setUploadingCat(categoryId);

      // Sanctum CSRF cookie (if you’re using it)
      await fetch("/sanctum/csrf-cookie", { credentials: "include" });

      const fd = new FormData();
      fd.append("category_id", categoryId);
      Array.from(list).forEach((f) => fd.append("files[]", f, f.name));

      // XSRF header is helpful with Sanctum; DO NOT set Content-Type manually
      const headers: HeadersInit = { Accept: "application/json" };
      const xsrf = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/)?.[1];
      if (xsrf) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrf);

      const resp = await fetch(`/api/documents/${project.id}/upload`, {
        method: "POST",
        credentials: "include",
        headers,
        body: fd,
      });

      if (!resp.ok) {
        const msg = await resp.text().catch(() => "");
        throw new Error(msg || `HTTP ${resp.status}`);
      }

      toast({
        title: "Success",
        description: "Files were successfully uploaded",
      });

      // After successful upload, refresh the project so the new docs appear
      const refreshed = await apiFetch(`/api/projects/${project.id}`);
      setProject(refreshed.data);

    } catch (err: any) {
      console.error(err);
      alert("Upload failed: " + (err?.message || err));
    } finally {
      setUploadingCat(null);
      if (fileInputRef.current) fileInputRef.current.value = ""; // allow re-selecting same files
    }
  }

async function enqueueReport() {
    // Use last good query values if inputs are empty (same pattern as Continue)
    const last = searchHistory[searchHistory.length - 1] || {};
    const payload: any = {
      project_id: project.id,
      // prefer current inputs; else last success; else undefined
      query:   (keywordQuery?.trim() || last?.query || undefined),
      aiquery: (refineQuery?.trim()  || last?.aiquery || undefined),
      // capture the current remaining paths (what the user would be “continuing”)
      paths: remainingAiPaths,
      // backend can infer user email from auth; no need to send it here
    };

    try {
      await apiFetch(`/api/projects/${project.id}/queue-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
      });
      // Hide the yellow retry div and show confirmation modal
      setReportQueued(true);
      setReportModalOpen(true);
    } catch (e) {
      console.error("Queue report failed", e);
      // optional: toast error
    }
  }

  const handleAddTag = (catId: string, fileId: string) => {
    if (!newTag.trim()) return;
    setProject(prev => ({
      ...prev,
      categories: prev.categories.map(cat =>
        cat.id === catId ? {
          ...cat,
          documents: cat.documents.map(f =>
            f.id === fileId ? { ...f, tags: [...(f.tags || []), newTag] } : f
          )
        } : cat
      )
    }));
    setNewTag("");
    setEditingFile("");
  };

  const handleRemoveTag = (catId: string, fileId: string, tagIdx: number) => {
    setProject(prev => ({
      ...prev,
      categories: prev.categories.map(cat =>
        cat.id === catId ? {
          ...cat,
          documents: cat.documents.map(f =>
            f.id === fileId ? { ...f, tags: (f.tags || []).filter((_, i) => i !== tagIdx) } : f
          )
        } : cat
      )
    }));
  };

  // NEW: latest keyword-filter index (last history entry with a non-empty `query`)
  const latestKeywordIdx = useMemo(() => {
    for (let i = searchHistory.length - 1; i >= 0; i--) {
      const q = searchHistory[i]?.query;
      if (typeof q === "string" && q.trim().length > 0) return i;
    }
    return -1;
  }, [searchHistory]);

  // NEW: remove that keyword-filter card and everything after it
  const handleRemoveKeywordFilteredFromIndex = (startIdx: number) => {
    if (startIdx < 0) return;
    const newHistory = searchHistory.slice(0, startIdx); // drop [startIdx..end]
    setSearchHistory(newHistory);

    // prune any matching items from results as well
    setSearchResults(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return [];
      const keepIds = new Set(newHistory.map(h => h.id));
      return prev.filter(r => keepIds.has(r.id));
    });

    if (newHistory.length === 0) {
      setShowResults(false);
      setIsRefineMode(false);
      setLastNotice(null);
    }
  };

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const committedKeywordQuery = keywordQuery.trim();
      const committedRefineQuery = refineQuery.trim();

      // Require something for a NEW search; allow refine-only when in refine mode
      if (!isRefineMode && !committedKeywordQuery && !committedRefineQuery) {
        setIsSearching(false);
        return;
      }

      const body: any = {
        query:   committedKeywordQuery || undefined,
        aiquery: committedRefineQuery  || undefined,
        category_id: selectedCategoryView === "0" ? 0 : parseInt(selectedCategoryView),
        limit: limitFilesReturned || undefined,
        or_search: orSearch || undefined,
        response_only: responseOnly || undefined,
      };

      // On refine, re-use the most recent successful paths
      if (isRefineMode && searchHistory.length > 0) {
        const previousPaths = getMostRecentPathsWithResults(searchHistory);
        if (previousPaths && previousPaths.length > 0) body.paths = previousPaths;
      }

      const data = await apiFetch<ApiSearchResponse & {
        processed_paths?: string[];
        unprocessed_paths?: string[];
        tpm_retry_after_ms?: number;
      }>(`/api/projects/${project.id}/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });

      const sourcesArr = Array.isArray(data.sources) ? data.sources : [];
      const hasDocs = (data.match_count ?? sourcesArr.length) > 0;
      const llmText = (data.llm_answer ?? "").toString();
      const hasLlm  = llmText.trim().length > 0;
      const hasAnyContent = hasDocs || hasLlm;

      // NEW: partial-processing info from backend
      const unprocessed = (data as any).unprocessed_paths ?? [];
      const retryMs     = Number((data as any).tpm_retry_after_ms ?? 0);

      if (hasAnyContent) {
        if (committedKeywordQuery) {
          keyLabelShow.current = true;
        } else {
          keyLabelShow.current = false;
        }

        const newEntry = {
          id: Date.now().toString(),
          query: committedKeywordQuery,
          aiquery: committedRefineQuery,               // <-- include for display
          fullResponse: data.response,
          sources: sourcesArr,
          llmAnswer: llmText,
        };

        setSearchHistory(prev => [...prev, newEntry]);
        setSearchResults([newEntry]);

        // Save prompts (non-blocking failures)
        // 1) keywords: split by comma, store each as type=0
        const tokens = splitKeywords(committedKeywordQuery);
        if (tokens.length) {
          await Promise.allSettled(tokens.map((t) =>
            apiFetch(`/api/projects/${project.id}/search-prompts`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: { prompt: t, type: 0 },
            })
          ));
        }
        // 2) ai prompt: whole string as type=1
        if (committedRefineQuery) {
          try {
            await apiFetch(`/api/projects/${project.id}/search-prompts`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: { prompt: committedRefineQuery, type: 1 },
            });
          } catch { /* ignore */ }
        }

        // Clear only the keyword box after success; keep refine text for iterative refinement
        setKeywordQuery("");

        // Enter refine mode if there is anything to work with
        setIsRefineMode(!!hasDocs || !!hasLlm);
        setShowResults(true);
        setLastNotice(null);

        // NEW: store remaining paths + start cooldown countdown (if any)
        setRemainingAiPaths(Array.isArray(unprocessed) ? unprocessed : []);
        setRetryAt(retryMs > 0 ? Date.now() + retryMs : 0);

        // Scroll the newest result to the top of the main container
        const mainEl = document.getElementById("body_main");
        requestAnimationFrame(() => {
          const node = lastResultRef.current;
          if (!mainEl || !node) return;
          const mainRect = mainEl.getBoundingClientRect();
          const nodeRect = node.getBoundingClientRect();
          const top = nodeRect.top - mainRect.top + mainEl.scrollTop;
          mainEl.scrollTo({ top, behavior: "smooth" });
        });
      } else {
        // No content
        if (!isRefineMode && searchHistory.length < 1) {
          setIsRefineMode(false);
          setShowResults(false);
          setSearchHistory([]);
          setSearchResults([]);
        }
        setLastNotice("No matches returned! Try adjusting your keywords or AI Query.");

        // Also clear any leftover partial-run state
        setRemainingAiPaths([]);
        setRetryAt(0);
      }
    } catch (error) {
      console.error("Search error:", error);
      // Optional: surface an inline error notice here
    } finally {
      setIsSearching(false);
    }
  };


  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleViewMode = (categoryId: string) => {
    setViewMode(prev => ({ ...prev, [categoryId]: prev[categoryId] === 'list' ? 'grid' : 'list' }));
  };

  function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
  }

  function getMostRecentPathsWithResults(history: Array<{ sources?: Array<{ path?: string }> }>): string[] | null {
    for (let i = history.length - 1; i >= 0; i--) {
      const srcs = history[i]?.sources;
      if (Array.isArray(srcs) && srcs.length > 0) {
        const paths = srcs.map(s => s?.path).filter((p): p is string => !!p);
        if (paths.length > 0) {
          const seen = new Set<string>();
          const unique = paths.filter(p => (seen.has(p) ? false : (seen.add(p), true)));
          return unique;
        }
      }
    }
    return null;
  }

const handleIncludeAllToggle = (checked: boolean) => {
  setIncludeAllDocs(checked);
  // Keep manual attachments intact for normal chat when the checkbox is cleared.
};

const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const paths = getMostRecentPathsWithResults(searchHistory);
      if (!paths || paths.length === 0) { alert("No files to export from the most recent results."); return; }

      await fetch("/sanctum/csrf-cookie", { credentials: "include" });
      const headers: HeadersInit = { Accept: "application/json", "Content-Type": "application/json" };
      const xsrfToken = getCookie("XSRF-TOKEN");
      if (xsrfToken) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);

      const response = await fetch(`/api/documents/${project.id}/export-query-files`, {
        method: "POST", credentials: "include", headers, body: JSON.stringify({ paths })
      });

      if (!response.ok) { const msg = await response.text().catch(() => ""); throw new Error(msg || `HTTP ${response.status}`); }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `project-${project.id}-search-results.zip`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert("Failed to export: " + (err?.message || err));
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportText = async () => {
    setIsExporting(true);
    try {
      const paths = getMostRecentPathsWithResults(searchHistory);
      if (!paths || paths.length === 0) { alert("No files to export from the most recent results."); return; }

      await fetch("/sanctum/csrf-cookie", { credentials: "include" });
      const headers: HeadersInit = { Accept: "application/json", "Content-Type": "application/json" };
      const xsrfToken = getCookie("XSRF-TOKEN");
      if (xsrfToken) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);

      const response = await fetch(`/api/documents/${project.id}/export-text-files`, {
        method: "POST", 
        credentials: "include", 
        headers, 
        body: JSON.stringify({ paths })
      });

      if (!response.ok) { 
        const msg = await response.text().catch(() => ""); 
        throw new Error(msg || `HTTP ${response.status}`); 
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); 
      a.href = url; 
      a.download = `project-${project.id}-text-export.zip`;
      document.body.appendChild(a); 
      a.click(); 
      a.remove(); 
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert("Failed to export text: " + (err?.message || err));
    } finally {
      setIsExporting(false);
    }
  };

  const handleAIAsk = async () => {
    const promptText = String(aiQuery ?? "").trim();      // ← renamed, safe coerce
    if (includeAllDocs) {
      if (!promptText || aiIsLoading) return;
      setAiIsLoading(true);
      setAiError(null);
      if (analysisRequest.current?.project !== project.id || analysisRequest.current?.prompt !== promptText) {
        analysisRequest.current = { project: project.id, prompt: promptText, key: crypto.randomUUID() };
      }
      try {
        await fetch("/sanctum/csrf-cookie", { credentials: "include" });
        const result = await apiFetch<{ id: number }>(`/api/projects/${project.id}/analyses`, {
          method: "POST", body: { prompt: promptText, request_key: analysisRequest.current.key },
        });
        setProjectAnalysisId(result.id);
        analysisRequest.current = null;
        setAiQuery("");
        toast({ title: "Full-project analysis queued", description: "Review progress, the completed report, and follow-up refinements on Reports." });
      } catch (e: any) {
        setAiError(e?.message || "Could not queue project analysis");
      } finally { setAiIsLoading(false); }
      return;
    }
    const filesToSend = Array.from(aiFilesRef.current.values());
    if (!promptText && filesToSend.length === 0) return;

    setAiIsLoading(true);
    setAiError(null);

    const nextHistory = [
      ...aiHistory,
      { role: "user", content: promptText || "(attachments only)" },
    ];
    setAiHistory(nextHistory);
    setAiQuery("");

    try {
      // Ensure Sanctum CSRF cookie exists
      await fetch("/sanctum/csrf-cookie", { credentials: "include" });

      const fd = new FormData();
      fd.append("project_id", String(project.id));

      nextHistory.forEach((m, i) => {
        fd.append(`messages[${i}][role]`, m.role);
        fd.append(`messages[${i}][content]`, m.content);
      });

      if (aiThreadId) fd.append("thread_id", aiThreadId);

      // Attach actual blobs from the ref (not from state)
      filesToSend.forEach((file) => fd.append("files[]", file, file.name));

      // Accept JSON; let browser set multipart boundary
      const headers: HeadersInit = {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      };
      const xsrf = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/)?.[1];
      if (xsrf) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrf);

      let fileCount = 0;
        for (const [k, v] of (fd as any).entries()) {
          if (k === 'files[]' && v && typeof (v as any).name === 'string') {
            fileCount++;
          }
        }
        console.log('[AI SEND] files in FormData:', fileCount);

      const resp = await fetch(`/api/projects/${project.id}/chat`, {
        method: "POST",
        credentials: "include",
        headers,
        body: fd,
      });

      if (!resp.ok) throw new Error(await resp.text());

      const data = await resp.json();

      const attachments: AiAttachment[] = Array.isArray(data.attachments)
        ? data.attachments
        : [];

      setAiHistory((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.message || "(no response)",
          attachments,
        },
      ]);

      if (data.thread_id && data.thread_id !== aiThreadId) {
        setAiThreadId(data.thread_id);
      }

      // Scroll newest result into view
      const aiEl = document.getElementById("ai_response");
      requestAnimationFrame(() => {
        const node = lastResultAiRef.current;
        if (!aiEl || !node) return;
        const aiRect = aiEl.getBoundingClientRect();
        const nodeRect = node.getBoundingClientRect();
        const top = nodeRect.top - aiRect.top + aiEl.scrollTop;
        aiEl.scrollTo({ top, behavior: "smooth" });
      });
    } catch (e: any) {
      console.error(e);
      setAiError(e?.message || "Chat failed");
    } finally {
      setAiIsLoading(false);
    }
  };

  const handleAttachmentRemove = useCallback((id: string) => {
    aiFilesRef.current.delete(id);                 // remove the real File
    setAiFileMeta(prev => prev.filter(m => m.id !== id)); // update UI list
  }, []);

  const handleAttachmentDownload = useCallback((name: string) => {
    // optional: no-op; real sending happens on "Send"
  }, []);

  const handleAiFilePick = (list: FileList | null) => {
    if (!list) return;
    const incoming = Array.from(list);

    const toAddMeta: AiFileMeta[] = [];
    const toAddFiles: Array<[string, File]> = [];

    for (const file of incoming) {
      const exists = Array.from(aiFilesRef.current.values()).some(f => f.name === file.name && f.size === file.size);
      if (exists) continue;

      const id = crypto.randomUUID?.() ?? `${file.name}:${file.size}:${Date.now()}`;
      toAddFiles.push([id, file]);
      toAddMeta.push({ id, name: file.name, size: file.size, type: file.type || "" });
    }

    for (const [id, f] of toAddFiles) aiFilesRef.current.set(id, f);
    setAiFileMeta(prev => [...prev, ...toAddMeta]);
  };

  const categoriesUI = useMemo(() => {
    return (
      <>
        {/* Categories Section */}
        <div className="space-y-4">
            <div className="flex items-center justify-between">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <Input value={editedName} onChange={(e) => setEditedName(e.target.value)} className="text-2xl font-bold border-none bg-transparent p-0 h-auto" />
                  <Button size="sm" onClick={() => { setProject(prev => ({ ...prev, name: editedName })); setIsEditingName(false); }}>
                    <Save className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setIsEditingName(false)}><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-3xl font-bold text-foreground">{project.name}</h1>
                  <Button size="sm" variant="ghost" onClick={() => setIsEditingName(true)}><Edit2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>

            {/* Toolbar with Select + New Category + (right side) Unprocessed badge + Process icon */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {project.categories?.length > 0 && (
                  <>
                    <label className="text-sm font-medium">Select Category:</label>
                    <Select value={selectedCategoryView} onValueChange={setSelectedCategoryView}>
                      <SelectTrigger className="w-[300px]"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem key="all" value="0">All Categories</SelectItem>
                        {project.categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name} ({category.documents?.length || 0} documents)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
                <Button onClick={() => {
                    const name = prompt("Category name:");
                    if (name) handleCreateCategory(name);
                }}><FolderPlus className="h-4 w-4 mr-2" />New Category</Button>
              </div>

              {/* RIGHT SIDE: status-aware badge + process icon */}
              {(
                unprocessedCount > 0 ||
                processStatus === 'processing' ||
                processStatus === 'error' ||
                (processStatus === 'processed' && !dismissProcessedBadge)
              ) && (
                <div className="flex items-center gap-2">
                  {processStatus === 'idle' && unprocessedCount > 0 && (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center">
                            <Badge variant="destructive" className="py-1 select-none">
                              <span className="text-base -mt-0.5 pr-1">{unprocessedCount}</span>
                              <FileClock className="ml-1 h-5 w-5 text-white pointer-events-none" />
                            </Badge>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">{unprocessedCount} unprocessed documents</TooltipContent>
                      </Tooltip>   
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center cursor-pointer">
                            <Button
                              size="icon"
                              variant="outline"
                              onClick={startProcessing}
                              disabled={isStartingProcessing}
                            >
                              {isStartingProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-5 w-5" />}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">Process Documents</TooltipContent>
                      </Tooltip>             
                    </>
                  )}

                  {processStatus === 'processing' && (
                    <Badge variant="secondary" className="py-2.5 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing documents…
                    </Badge>
                  )}

                  {processStatus === 'processed' && !dismissProcessedBadge && (
                    <Badge variant="default" className="py-2.5 flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        {processedFilesCount} Documents processed!
                        <button
                          className="ml-1 hover:opacity-100 cursor-pointer"
                          aria-label="Dismiss"
                          title="Dismiss"
                          onClick={async () => {
                            try {
                              await dismissProcessQueue();
                            } catch (e) {
                              // optional: toast an error
                              console.error(e);
                            } finally {
                              setDismissProcessedBadge(true);
                              setProcessStatus('idle');
                              setProcessedFilesCount(0);
                            }
                          }}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                    </Badge>
                  )}

                  {processStatus === 'error' && (
                    <>
                      <Badge variant="destructive" className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Error, not all documents processed
                      </Badge>
                      <Button
                        size="icon"
                        variant="outline"
                        title="Retry Processing"
                        onClick={startProcessing}
                        disabled={isStartingProcessing}
                      >
                        {isStartingProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-5 w-5" />}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {showNewCategory && (
              <Card className="border border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Category name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (async () => {
                        const trimmedName = newCategoryName.trim();
                        if (!trimmedName) return;
                        try {
                          const response = await apiFetch(`/api/projects/${project.id}/create-category`, {
                            method: "POST",
                            body: { name: trimmedName }
                          });
                          const createdCategory = (response as any).data;
                          setProject(prev => ({ ...prev, categories: [...prev.categories, createdCategory] }));
                          setNewCategoryName("");
                          setShowNewCategory(false);
                        } catch (error: any) {
                          alert("Error creating category");
                        }
                      })()}
                    />
                    <Button onClick={async () => {
                      const trimmedName = newCategoryName.trim();
                      if (!trimmedName) return;
                      try {
                        const response = await apiFetch(`/api/projects/${project.id}/create-category`, {
                          method: "POST",
                          body: { name: trimmedName }
                        });
                        const createdCategory = (response as any).data;
                        setProject(prev => ({ ...prev, categories: [...prev.categories, createdCategory] }));
                        setNewCategoryName("");
                        setShowNewCategory(false);
                      } catch {
                        alert("Error creating category");
                      }
                    }}>Create</Button>
                    <Button variant="ghost" onClick={() => setShowNewCategory(false)}><X className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-4">
              {selectedCategoryView && (project.categories || [])
                .filter(category => category.id === selectedCategoryView)
                .map((category) => (
                  <Card key={category.id} className="relative border border-border">
                    <CardHeader className="p-4">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <FolderPlus className="h-5 w-5" />
                          {category.name}
                          <Badge variant="secondary">{category.documents?.length} documents</Badge>
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => { setSelectedCategory(category.id); fileInputRef.current?.click(); }}
                            disabled={uploadingCat === category.id}
                          >
                            {uploadingCat === category.id ? (
                              <span className="inline-flex items-center">
                                <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current mr-2" />
                                Uploading…
                              </span>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Files
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {category.documents?.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">No files uploaded yet</p>
                      ) : (
                        <>
                            <div
                            className={`flex items-center cursor-pointer mb-2 ${isDocsRendering ? "pointer-events-none opacity-70" : ""}`}
                            onClick={() => {
                              const isHuge = (category.documents?.length ?? 0) > 1000;
                              if (!expanded && isHuge) {
                                setIsDocsRendering(true);
                                // Let the spinner paint before mounting the big list
                                requestAnimationFrame(() => {
                                  setExpanded(true);
                                  setTimeout(() => setIsDocsRendering(false), 500);
                                });
                              } else {
                                setExpanded(!expanded);
                              }
                            }}
                            >
                            <h3 className="text-lg font-semibold mr-2 flex items-center gap-2">
                              {expanded ? "Hide Documents" : "View Documents"}
                              {isDocsRendering && (
                                <span
                                  className="inline-block animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"
                                  aria-label="Loading documents"
                                />
                              )}
                            </h3>
                            {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                          </div>
                          {expanded && (
                            <div className="p-6 pt-0 pb-0 transition-all duration-300 ease-in-out">
                              <div className="max-h-[210px] overflow-y-auto">
                                <div className={viewMode[category.id] === 'list' ? 'space-y-2' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
                                  {category.documents?.map((document) => (
                                    <div key={document.id} className={`border border-border pt-0 pb-0 rounded-lg p-2 ${viewMode[category.id] === 'list' ? 'flex items-center justify-between' : 'space-y-2'}`}>
                                      <div className={viewMode[category.id] === 'list' ? 'flex items-center gap-3' : 'space-y-2'}>                                       
                                        {document.processed ? (
                                          <>
                                            <input
                                              type="checkbox"
                                              className="h-4 w-4 accent-primary"
                                              checked={selectedDocIds.has(document.id)}
                                              onChange={() => toggleDocChecked(document.id)}
                                              aria-label={`Select ${document.name}`}
                                            />
                                            <File
                                              className="h-5 w-5 text-primary pointer-events-none"
                                              title="Processed"
                                              aria-hidden="true"
                                            />
                                          </>
                                        ) : (
                                          <FileClock
                                            className="h-5 w-5 text-red-500 pointer-events-none"
                                            title="Not processed"
                                            aria-label="Not processed"
                                          >
                                            <title>Not processed</title>
                                          </FileClock>
                                        )}
                                        <div>
                                          <a href={`/api/documents/${document.id}/download`} className="text-sm">{document.name}</a>
                                          <p className="text-sm text-muted-foreground">
                                            {formatFileSize(document.size)} • {new Date(document.uploadDate).toLocaleDateString()}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="space-y-2">
                                        <div className="flex flex-wrap gap-1">
                                          {(document.tags || []).map((tag, tagIndex) => (
                                            <Badge key={tagIndex} variant="secondary" className="text-xs">
                                              {tag}
                                              <Button size="sm" variant="ghost" className="h-auto p-0 ml-1" onClick={() => {
                                                setProject(prev => ({
                                                  ...prev,
                                                  categories: prev.categories.map(cat =>
                                                    cat.id === category.id ? {
                                                      ...cat,
                                                      documents: cat.documents.map(f =>
                                                        f.id === document.id ? { ...f, tags: (f.tags || []).filter((_, i) => i !== tagIndex) } : f
                                                      )
                                                    } : cat
                                                  )
                                                }));
                                              }}>
                                                <X className="h-3 w-3" />
                                              </Button>
                                            </Badge>
                                          ))}
                                        </div>
                                        {editingFile === document.id ? (
                                          <div className="flex items-center gap-2">
                                            <Input placeholder="Add tag" value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddTag(category.id, document.id)} />
                                            <Button size="sm" onClick={() => handleAddTag(category.id, document.id)}><Plus className="h-3 w-3" /></Button>
                                          </div>
                                        ) : (
                                          <Button size="sm" variant="ghost" onClick={() => setEditingFile(document.id)}>
                                            <Tag className="h-4 w-4 mr-1" />Add Tag
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                    <CardFooter className="p-4">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2"></CardTitle>
                        <div className="flex items-center gap-2">
                            {selectedDocIds.size > 0 && (
                              <div className="absolute bottom-3 pl-7 left-3 flex gap-2">
                                <Button
                                  className="ml-2.5"
                                  size="sm"
                                  onClick={() => {
                                    includeSelectedIntoKeywords(category.documents);
                                    requestAnimationFrame(scrollToBottom);
                                  }}
                                  title="Add selected filenames to Keywords"
                                >
                                  Include in Keywords
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => uploadSelectedToGeneralAI(category.documents)}
                                  disabled={aiDocsIsLoading}
                                  title="Attach the selected files to General AI chat"
                                >
                                {aiDocsIsLoading ? (
                                    <div className="animate-spin text-current rounded-full h-5 w-5 border-t-2 border-current border-b-2 border-background mr-2" />
                                ) : (
                                    <Paperclip className="h-3.5 w-3.5 mr-1" />
                                )}
                                  Upload to GeneralAI
                                </Button>
                              </div>
                            )}
                        </div>
                      </div>
                    </CardFooter>
                  </Card>
                ))}

              {!selectedCategoryView && (project.categories || []).length > 0 && (
                <p className="text-muted-foreground text-center py-8">Please select a category above to view its contents</p>
              )}
            </div>
          </div>
      </>
    );
    // Only re-render this when these *really* change:
  }, [
    project,
    selectedCategoryView,
    expanded,
    isDocsRendering,
    uploadingCat,
    selectedDocIds,
    aiDocsIsLoading,
    processStatus,
    dismissProcessedBadge,
    processedFilesCount,
    isStartingProcessing,
  ]);

  return (
    <div id="query_main">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <Button className="px-0" variant="ghost" size="sm" onClick={() => navigate("/dashboard/projects")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
      </div>

      {/* Categories Section */}
      {categoriesUI}

{/* Query Section with Tabs */}
      <div id="query_section" className="space-y-4 mt-4 mb-[2rem]">
        <Card className="border border-border">
          <CardContent className="p-4 space-y-4">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'doc' | 'ai')}>
              <TabsList>
                <TabsTrigger value="doc" className="data-[state=active]:font-bold">&nbsp; &nbsp;Document Queries&nbsp; &nbsp;</TabsTrigger>
                <TabsTrigger value="ai" className="data-[state=active]:font-bold">&nbsp; &nbsp;General AI Queries&nbsp; &nbsp;</TabsTrigger>
              </TabsList>

              {/* --- Tab 1: Document Queries --- */}
              <TabsContent value="doc" className="space-y-4">
                {showResults && searchHistory.length > 0 && (
                  <>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 id="search_results" className="font-medium">Search Results</h3>
                      </div>
                      <div className="space-y-3">
                        {searchHistory.map((result, idx) => {
                          const isLast = idx === searchHistory.length - 1;
                          const isLatestKeyword = idx === latestKeywordIdx; // show delete only on the latest keyword-filter card
                          return(
                          <Card key={result.id} className="hover:bg-accent">
                            <CardContent className="p-4 relative" ref={isLast ? lastResultRef : undefined}>
                              {/* Delete icon for the latest keyword-filter card */}
                              {isLatestKeyword && (
                                <Button
                                  size="icon"
                                  variant="destructive"
                                  className="absolute mr-10 p-1 w-7 h-7 top-4 top-4 right-2"
                                  title="Remove Filtered Results"
                                  onClick={() => handleRemoveKeywordFilteredFromIndex(idx)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                {result.llmAnswer && result.llmAnswer.trim() !== "" && (
                                <>
                                  <div className="text-s italic text-muted-foreground">AI Query: {result.aiquery}</div>
                                  <div className="rounded-md bg-muted p-3 whitespace-pre-wrap">
                                    <div className="font-medium mb-1">AI Answer</div>
                                    <div className="text-muted-foreground">{result.llmAnswer}</div>
                                  </div>
                                </>
                                )}
                                <div id="query_label" className={`text-s italic text-muted-foreground ${keyLabelShow.current ? "" : "hidden"}`}>Keywords: {result.query}</div>
                                {result.fullResponse && (<p className="text-sm text-muted-foreground font-bold">{result.fullResponse}</p>)}
                                {result.sources.map((source: any, index: number) => (
                                  <div key={index} className="mt-2">
                                    <div className="flex items-center gap-2">
                                      <FileIcon className="h-4 w-4 text-primary" />
                                      <span className="font-medium">
                                        <a href={`/api/documents/download-by-path?path=${encodeURIComponent(source.path)}`}>
                                          {source.path.split('/').pop()}
                                        </a>
                                      </span>
                                      {source.redacted && (<Badge variant="destructive" className="text-xs">Redacted</Badge>)}
                                      {source.privileged && (<Badge variant="destructive" className="text-xs">Privileged</Badge>)}
                                    </div>
                                    {source.match && (<div className="text-s text-muted-foreground">Excerpt: &quot;<Html html={source.match} />&quot;</div>)}
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                          );
                        })}
                      </div>
                    </div>
                    {isExporting ? (
                      <div className="flex justify-center items-center w-full">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button variant="default" onClick={handleExportAll}>Export All</Button>
                        <Button variant="outline" onClick={handleExportText}>Export as Text</Button>
                      </div>
                    )}
                  </>
                )}
                {remainingAiPaths.length > 0 && !reportQueued && (
                  <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm flex items-center justify-between">
                    <div>
                      {secsLeft > 0 ? (
                        <>Processed a subset to stay under token limits. {remainingAiPaths.length} files remain. Evaluation can continue in <strong>{secsLeft}s</strong>.</>
                      ) : (
                        <>Processed a subset to stay under token limits. {remainingAiPaths.length} files remain.</>
                      )}
                    </div>
                    <div class="ml-auto flex items-center gap-2">
                        <Button
                            size="sm"
                            disabled={secsLeft > 0 || isContinuing}
                            onClick={async () => {
                              setIsContinuing(true);

                              // If inputs are empty, reuse last successful prompts from history
                              const last = searchHistory[searchHistory.length - 1] || {};
                              const priorQuery   = (typeof last?.query === "string"   && last.query.trim())   ? last.query.trim()   : undefined;
                              const priorAiQuery = (typeof last?.aiquery === "string" && last.aiquery.trim()) ? last.aiquery.trim() : undefined;

                              const body: any = {
                                query:   (keywordQuery.trim() || priorQuery || undefined),
                                aiquery: (refineQuery.trim()  || priorAiQuery || undefined),
                                paths: remainingAiPaths,
                                category_id: selectedCategoryView === "0" ? 0 : parseInt(selectedCategoryView),
                                limit: limitFilesReturned || undefined,
                                or_search: orSearch || undefined,
                                response_only: responseOnly || undefined,
                              };

                              // Start/refresh cooldown immediately so the banner ticks right away
                              setRetryAt(Date.now() + 60000);

                              try {
                                const data = await apiFetch(`/api/projects/${project.id}/query`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body,
                                });

                                const incomingSources = Array.isArray((data as any).sources) ? (data as any).sources : [];
                                const incomingAnswer  = ((data as any).llm_answer ?? "").toString();
                                const hasAnyContent   = incomingSources.length > 0 || incomingAnswer.trim().length > 0;

                                // Always update remaining & retry timers from the server
                                const remaining = Array.isArray((data as any).unprocessed_paths) ? (data as any).unprocessed_paths : [];
                                const retryMs   = Number((data as any).tpm_retry_after_ms || 0);
                                setRemainingAiPaths(remaining);
                                setRetryAt(retryMs > 0 ? Date.now() + retryMs : 0);

                                if (!hasAnyContent) {
                                  // No new sources/answer this round (pure “keep waiting” response) — leave the card alone.
                                  return;
                                }

                                // Merge into the last history entry and keep Results view in sync
                                setSearchHistory(prev => {
                                  if (prev.length === 0) return prev;
                                  const copy = [...prev];
                                  const lastEntry = { ...copy[copy.length - 1] };

                                  // 1) Merge sources by unique path
                                  const existing = Array.isArray(lastEntry.sources) ? lastEntry.sources : [];
                                  const seen = new Set<string>();
                                  const merged = [...existing, ...incomingSources].filter(s => {
                                    const p = s?.path || "";
                                    if (!p || seen.has(p)) return false;
                                    seen.add(p);
                                    return true;
                                  });
                                  lastEntry.sources = merged;

                                  // 2) Append continued AI answer
                                  if (incomingAnswer.trim().length > 0) {
                                    const prior = (lastEntry.llmAnswer || "").toString().trim();
                                    lastEntry.llmAnswer = prior ? `${prior}\n\n--- continuation ---\n\n${incomingAnswer}` : incomingAnswer;
                                  }

                                  copy[copy.length - 1] = lastEntry;

                                  // Keep the visible “Search Results” list aligned with history (last card only)
                                  setSearchResults([lastEntry]);
                                  return copy;
                                });

                                // Auto-scroll the updated card back into view
                                const mainEl = document.getElementById("body_main");
                                requestAnimationFrame(() => {
                                  const node = lastResultRef.current;
                                  if (!mainEl || !node) return;
                                  const mainRect = mainEl.getBoundingClientRect();
                                  const nodeRect = node.getBoundingClientRect();
                                  const top = nodeRect.top - mainRect.top + mainEl.scrollTop;
                                  mainEl.scrollTo({ top, behavior: "smooth" });
                                });
                              } catch (err) {
                                console.error("Continue failed:", err);
                                // Back off if likely rate-limited again
                                setRetryAt(Date.now() + 60000);
                              } finally {
                                setIsContinuing(false);
                              }
                            }}
                          >
                            {isContinuing ? (
                              <span className="inline-flex items-center">
                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-background mr-2" />
                                Continuing…
                              </span>
                            ) : (
                              secsLeft > 0 ? "Please wait..." : "Continue AI Analysis"
                            )}
                          </Button>
                          <Button
                              size="sm"
                              variant="secondary"
                              onClick={enqueueReport}
                          >
                              Send me a Report
                          </Button>
                    </div>
                  </div>
                )}

                {/* NEW top textarea + radios (always visible) */}
                {lastNotice && (<div className="text-sm font-bold text-muted-foreground mt-2">{lastNotice}</div>)}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-muted-foreground">Find documents by keywords:</label>
                  <div className="flex items-center gap-4 -mt-1">
                    <label className="flex items-center space-x-1">
                      <input type="radio" name="logicType" value="" checked={!orSearch} onChange={() => setOrSearch(false)} />
                      <span className="text-sm font-medium text-muted-foreground">Include ALL Keywords</span>
                    </label>
                    <label className="flex items-center space-x-1">
                      <input type="radio" name="logicType" value="or" checked={!!orSearch} onChange={() => setOrSearch(true)} />
                      <span className="text-sm font-medium text-muted-foreground">Include ANY Keyword</span>
                    </label>
                  </div>
                  <Textarea
                    rows={4}
                    placeholder={
                      isRefineMode
                        ? 'Enter more keywords to refine the document results set, and/or add an AI prompt below.'
                        : 'Enter keywords separated by a comma and enclose phrases in quotes\nExample 1: abc@somewhere.com, xyz@somewhere.com\nExample 2: redacted, restricted, "showed malice"\n'
                    }
                    value={keywordQuery}
                    onKeyDown={() => { kwTypedRef.current = true; }}   // user started typing
                    onPaste={() => { kwTypedRef.current = true; }} 
                    onBlur={() => { kwTypedRef.current = false; }}     // lost focus -> not typing
                    onChange={(e) => {
                      // Only react to real typing
                      if (!kwTypedRef.current) return;
                      setKeywordQuery(e.currentTarget.value);
                    }}
                    className="w-full resize-none"
                  />
                  {/* Keyword suggestions (type=0) — based on the last token */}
                  {showKeywordSuggestions && keywordSuggestions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {keywordSuggestions.map((s, i) => (
                        <Button
                          key={`kw-${s.prompt}-${i}`}
                          size="sm"
                          variant="outline"
                          onPointerDown={() => { kwTypedRef.current = false; }}
                          onClick={() => { 
                            suppressKwSuggestOnce.current = true;
                            setKeywordQuery(prev => applyKeywordSuggestion(prev, s.prompt)); 
                            clearAllSuggestions(); }}
                          title={`Used ${s.uses} time${s.uses === 1 ? '' : 's'}`}
                        >
                          {s.prompt}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                {/* textarea (AI refine). */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">AI Prompt to perform on returned documents:</label>
                    <Textarea
                      rows={6}
                      placeholder={'Refine the active documents result set...\nExample: which of these documents contain data that appears to be from emails\nExample2: show documents that contain dates from 10/15/2021 through 01/01/2022\nExample3: how many times does the phrase "attempted bribery" appear in these documents'}
                      value={refineQuery}
                        onKeyDown={() => { aiTypedRef.current = true; }}   // user started typing
                        onPaste={() => { aiTypedRef.current = true; }} 
                        onBlur={() => { aiTypedRef.current = false; }}     // lost focus -> not typing
                        onChange={(e) => {
                          // Only react to real typing
                          if (!aiTypedRef.current) return;
                          setRefineQuery(e.currentTarget.value);
                      }}
                      className="w-full resize-none"
                    />

                    {/* AI prompt suggestions (type=1) */}
                    {refineQuery && showAiPromptSuggestions && aiPromptSuggestions.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {aiPromptSuggestions.map((s, i) => (
                          <Button
                            key={`ai-${s.prompt}-${i}`}
                            size="sm"
                            variant="outline"
                            onPointerDown={() => { aiTypedRef.current = false; }}
                            onClick={() => { 
                              suppressAiSuggestOnce.current = true;
                              setRefineQuery(s.prompt); 
                              clearAllSuggestions(); }}
                            title={`Used ${s.uses} time${s.uses === 1 ? '' : 's'}`}
                          >
                            {s.prompt}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>

                {/* Controls row */}
                <div className="flex items-center gap-2 min-h-[3rem]">
                  {searchHistory.length > 0 && !isSearching && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchHistory([]);
                        setKeywordQuery("");
                        setRefineQuery("");
                        setIsRefineMode(false);
                        setShowResults(false);
                        setLastNotice(null);
                      }}
                      className="shrink-0"
                    >
                      New Search
                    </Button>
                  )}

                  <div className="flex items-center gap-2 ml-auto">
                    {/* <Button variant="outline" size="icon" onClick={() => setShowFiltersModal(true)}>
                      <Filter className="h-4 w-4" />
                    </Button> */}

                    {searchHistory.length > 0 ? (
                      <Button onClick={handleSearch} disabled={isSearching}>
                        {isSearching ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-background mr-2" />
                        ) : (
                          <Search className="h-4 w-4 mr-2" />
                        )}
                        Refine Search
                      </Button>
                    ) : (
                      <Button onClick={handleSearch} disabled={isSearching}>
                        {isSearching ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-background mr-2" />
                        ) : (                        
                          <Search className="h-4 w-4 mr-2" />
                        )}
                        Search
                      </Button>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* --- Tab 2: General AI Queries --- */}
              <TabsContent value="ai" className="space-y-4">
                <div className="space-y-3">
                  {aiHistory.some(m => m.role === "assistant") && (
                  <div id="ai_response" className="border rounded-md p-3 h-72 overflow-y-auto bg-muted/30">
                      <AiHistoryList history={aiHistory} lastRef={lastResultAiRef} />
                  </div>
                  )}
                  {aiError && (<div className="text-sm text-red-600">{aiError}</div>)}
                  <div className="flex items-start gap-2">
                    <Textarea
                      rows={4}
                      placeholder="Ask anything..."
                      value={aiQuery}
                      onChange={(e) => setAiQuery(e.currentTarget.value)}
                      className="w-full resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          handleAIAsk();
                        }
                      }}
                    />

                    <div className="flex flex-col gap-2 shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => aiFileInputRef.current?.click()}
                        disabled={aiIsLoading || includeAllDocs}
                        title="Attach files"
                      >
                        <Paperclip className="h-4 w-4 mr-2" />
                        Attach
                      </Button>

                      <input
                        ref={aiFileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => handleAiFilePick(e.target.files)}
                      />

                      <Button onClick={handleAIAsk} disabled={aiIsLoading}>
                        {aiIsLoading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-background mr-2" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        {includeAllDocs ? "Analyze project" : "Send"}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                      <input
                        id="include_all_docs"
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={includeAllDocs}
                        onChange={(e) => handleIncludeAllToggle(e.currentTarget.checked)}
                      />
                      <label htmlFor="include_all_docs" className="text-sm select-none">
                        Include all project documents
                      </label>
                  </div>
                  {includeAllDocs && <p className="text-sm text-muted-foreground">
                    Every project document will be reviewed in the background. The report and follow-up refinements are available on Reports.
                    {aiFileMeta.length > 0 && " Your manual attachments are preserved for regular chat and are not included in this project analysis."}
                  </p>}
                  {projectAnalysisId && <Button variant="outline" onClick={() => navigate(`/reports?analysis=${projectAnalysisId}`)}>
                    View project analysis #{projectAnalysisId}
                  </Button>}
                  {/* show selected files */}
                  {aiFileMeta.length > 0 && (
                    <AttachmentsList
                      items={aiFileMeta}
                      onRemove={handleAttachmentRemove}
                      onDownload={handleAttachmentDownload}
                    />
                  )}
                  {aiHistory.length > 0 && (<div className="flex gap-2">
                <Button variant="ghost" size="sm" 
                    onClick={() => {
                        setAiHistory([]);
                        setAiFileMeta([]);        // clears the UI list
                        aiFilesRef.current.clear(); // (optional) also clear the actual File blobs
                        setAiThreadId(null);    // (optional) start a fresh assistant thread
                    }}                    
                >Clear Chat</Button></div>)}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <div ref={bottomRef} />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (!e.target.files) return;
          if (!selectedCategory) {
            alert("Please click 'Upload Files' from a specific category.");
            e.target.value = "";
            return;
          }
          // keep your existing uploadSelectedFiles(categoryId, files)
          // @ts-expect-error: function defined in your original file
          uploadSelectedFiles(selectedCategory, e.target.files);
        }}
      />	
      {/* Filters modal */}
      <Dialog open={showFiltersModal} onOpenChange={setShowFiltersModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Filters</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label htmlFor="limitInput" className="inline-block w-[135px] text-sm font-semibold text-muted-foreground">Limit Files Returned:</label>
              <Input id="limitInput" type="number" min="1" placeholder="50" className="w-24 h-8 px-2 py-1 text-sm" value={limitFilesReturned || ""} onChange={(e) => setLimitFilesReturned(Number(e.target.value))} />
              {limitFilesReturned && (
                <Button variant="ghost" size="sm" onClick={() => setLimitFilesReturned(undefined)} className="text-sm">Clear Filters</Button>
              )}
            </div>

            {isRefineMode && (
              <div className="flex items-center gap-2">
                <label htmlFor="responseType" className="inline-block w-[135px] text-sm font-semibold text-muted-foreground">Response Type:</label>
                <label className="flex items-center space-x-1">
                  <input type="radio" name="responseType" value="" checked={!responseOnly} onChange={() => setResponseOnly(false)} />
                  <span className="text-sm font-medium text-muted-foreground">Documents and Response</span>
                </label>
                <label className="flex items-center space-x-1">
                  <input type="radio" name="responseType" value="response" checked={!!responseOnly} onChange={() => setResponseOnly(true)} />
                  <span className="text-sm font-medium text-muted-foreground">Response Only</span>
                </label>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={() => setShowFiltersModal(false)}>Apply Filters</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Queued</DialogTitle>
            <DialogDescription>
              The full AI response to your query has been queued for processing. 
              You’ll get an email when it’s ready.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Project;
